import { BlogPost } from '@/types/blog';

export const post: BlogPost = {
  slug: "introducing-fordje-reports",
  title: "Introducing Fordje Reports: Compare Multiple Markets in Minutes",
  excerpt: "Quickly scan regulatory requirements across cities — within a region or across the U.S. — so you know where you can expand with confidence.",
  content: `
    <p>
      Expanding into new regions means navigating unfamiliar regulations. One city over, and suddenly your standard build approach hits unexpected requirements — setbacks that don't work, timelines that derail your schedule, codes that force costly redesigns.
    </p>
    
    <p>
      The real risk isn't just slow research. It's building based on incomplete information, then getting stuck in a regulatory quagmire after you've already committed.
    </p>
    
    <p>
      Teams expanding geographically face the same grind: research City A, then City B, then City C. Same questions (setbacks, timelines, fire codes, permit processes), same manual process, different cities. By the time you've compared 5-10 options, you've burned days — and still might not have the full picture.
    </p>
    
    <p class="text-xl md:text-2xl font-semibold text-fordje-navy my-6">
      Today, we're launching Fordje Reports.
    </p>
    
    <div class="my-6">
      <a href="/lovable-uploads/fordje-reports-cover.png" target="_blank" rel="noopener noreferrer" class="block max-w-md mx-auto hover:opacity-90 transition-opacity cursor-zoom-in">
        <img src="/lovable-uploads/fordje-reports-cover.png" alt="Fordje Reports interface showing side-by-side city comparison" class="rounded-lg shadow-md border border-gray-200" />
        <p class="text-sm text-center text-muted-foreground mt-2">Click to view full size</p>
      </a>
    </div>
    
    <p>
      Reports lets you quickly scan regulatory requirements across cities — within a region or across the U.S. — so you know where you can expand with confidence.
    </p>
    
    <h2>How It Works</h2>
    
    <p>
      <strong>Choose what matters</strong><br/>
      Start with pre-built templates (Residential Housing, Clean Tech, Venue Selection) or build your own with the code extracts that matter to your projects — setbacks, fire codes, timelines, permit requirements, whatever your team needs to evaluate.
    </p>
    
    <p>
      <strong>Pick your cities</strong><br/>
      Select the cities you're evaluating — whether comparing within a metro or across regions. Reports gives you the flexibility to see the landscape however you need it.
    </p>
    
    <p>
      <strong>Customize and compare</strong><br/>
      Adjust your extract selections as needed, then scan requirements side-by-side, grouped for easy understanding, with clear references showing exactly where each requirement lives in the source documents.
    </p>
    
    <p>
      <strong>Export and share</strong><br/>
      Download the full report to share with your team for exec summaries, planning meetings, or internal coordination.
    </p>
    
    <h2>What It Means for Your Team</h2>
    
    <p>
      <strong>Compare markets in minutes, not days</strong><br/>
      Scan opportunities across entire metros or regions. See the full landscape before you commit.
    </p>
    
    <p>
      <strong>Hunt for green flags</strong><br/>
      Find cities with requirements that work with what you build — generous setbacks, compatible processes, flexible FAR — requirements that work with your standard build for faster timelines.
    </p>
    
    <p>
      <strong>Spot red flags early</strong><br/>
      See deal-breakers before you commit resources. Understand what won't work before you're locked in.
    </p>
    
    <p>
      <strong>Get everyone aligned</strong><br/>
      See all requirements from acquisition through permitting to build, so every team knows what each project needs from the start. Export reports for exec summaries, planning meetings, or internal coordination.
    </p>
    
    <h2>Built for How Teams Actually Work</h2>
    
    <p>
      Whether you're a VP of Operations evaluating new markets, an innovation team assessing product viability across regions, or a multi-city operator keeping teams aligned, Reports helps you understand requirements faster and build with confidence.
    </p>
    
    <p>
      <strong>Fordje Reports is available now.</strong> <a href="/get-started" class="text-fordje-blue hover:text-fordje-accent underline">Request a demo</a> to see it in action.
    </p>
  `,
  coverImage: "/lovable-uploads/fordje-reports-cover.png",
  date: "2026-02-05T12:00:00",
  readTime: 4,
  categories: ["Product", "Company News"],
  author: {
    name: "Gillian Sowray",
    title: "CEO & Co-Founder",
    avatar: "/lovable-uploads/69c2731c-f0dc-41d3-8812-8b392d7302c2.png"
  },
  metaDescription: "Introducing Fordje Reports: quickly scan and compare regulatory requirements across multiple cities to expand into new markets with confidence.",
  seoKeywords: ["regulatory comparison", "market expansion", "building codes", "city regulations", "Fordje Reports"]
};
